"use client";

import { IBaseLabelValue, TIbanCard } from "@/interfaces";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ibanCardSchema } from "@/validations";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import { cn } from "@/lib";
import { useRouter } from "next/navigation";
import ResultContent from "../../resultContent";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import DetailCreditCard from "../../detailCreditCard";
import DetailInquiryPdf from "../../pdfTemplates/detailInquiryPdf";
import { useRef } from "react";
import { IRawPurchasedIbanInquiryResData } from "@/interfaces/inquiries/bank";

const IbanInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedIbanInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const { bank, owners, depositNumber, status } = data.data.data;
  const { value: iban } = data.data.input;
  const formattedIban = iban?.replace(/^IR/, "");
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const { control } = useForm<TIbanCard>({
    resolver: zodResolver(ibanCardSchema),
    defaultValues: {
      input1: formattedIban?.slice(0, 4),
      input2: formattedIban?.slice(4, 8),
      input3: formattedIban?.slice(8, 12),
      input4: formattedIban?.slice(12, 16),
      input5: formattedIban?.slice(16, 20),
      input6: formattedIban?.slice(20, 24),
    },
  });

  const joinedOwners = owners
    .map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    .join(",");

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("ibanInquiry.accountOwners"),
      value: joinedOwners,
    },
    {
      label: t("ibanInquiry.bankName"),
      value: bank,
    },
    {
      label: t("ibanInquiry.depositNumber"),
      value: depositNumber,
    },
    {
      label: t("ibanInquiry.accountStatus"),
      value: status,
    },
  ];

  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("ibanInquiry.title")}
        </h2>

        <div className="mx-auto mb-8 sm:w-3/4">
          <DetailCreditCard
            variant="iban"
            owners={joinedOwners}
            cardValues={{
              label: t("cardToIban.ibanNumber"),
              value: formattedIban,
            }}
            footer={() => (
              <>
                <div className="flex w-full justify-between">
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">شماره حساب</span>
                    <span
                      className="md:text-md text-sm font-semibold"
                      dir="ltr"
                    >
                      {depositNumber}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">وضعیت حساب</span>
                    <span className="md:text-md text-sm font-semibold">
                      {status}
                    </span>
                  </div>
                </div>
              </>
            )}
          />
        </div>

        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center font-bold lg:text-lg">
            {t("ibanInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="iban-inquiry-download-pdf"
            fileName="استعلام شبا"
            tableTitle="شبا"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>

        <ResultContent content={resultContent} />
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            id="iban-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            fileName="استعلام شبا"
            tableTitle="شبا"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-iban-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-iban-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Iban_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <DetailInquiryPdf
          subtitle="استعلام شبا"
          code={data.code}
          displayComponent={{
            variant: "iban",
            cardNumber: formattedIban,
            ownerName: joinedOwners,
            mainValue: {
              label: "شماره شبا",
              value: formattedIban,
            },
            bankName: bank,
            additionalFields: [
              {
                label: "شماره حساب",
                value: depositNumber,
              },
              {
                label: "وضعیت حساب",
                value: status,
              },
            ],
          }}
          resultContent={resultContent}
          showResultSection={true}
        />
      </div>
    </div>
  );
};

export default IbanInquiryDetails;
